import { useContext, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { translate } from '../../../core'
import { PageContextValue } from '../../../core/type'
import { Order, OrderEntry, OrderFindRequest } from '../type'
import { OrderPageContext } from './OrderPageContext'
import { GetOrderPage } from '../graphql/order-queries.graphql'
import { SearchModal, SearchProps } from '../../../core/component/Search'
import { SearchTargetConfig } from '../../../core/component/Search/type'
import { GetCustomersForLookup } from '../../contact/graphql/contact.queries.graphql'
import { Select } from 'antd'

const SEARCH_TARGET_CONTACT = 'contact'
const SEARCH_TARGET_ORDER = 'order'

export const OrderSearch = ({ open, closeMe }: SearchProps) => {
  const { fetch } = useContext<PageContextValue<Order, OrderEntry>>(OrderPageContext)
  const [getPage, { loading }] = useLazyQuery<{ orderPage: number }, {
    number: number
  }>(GetOrderPage, { errorPolicy: 'all' })

  const [searchCustomer, setSearchCustomer] = useState<string>()

  const { data: customers, loading: loadingCustomers } = useQuery(GetCustomersForLookup, {
    variables: {
      request: {
        isCustomer: true,
        take: 20,
        skip: 0,
        contactId: searchCustomer,
      },
    },
  })

  const searchTarget: SearchTargetConfig<OrderFindRequest> = {
    options: [
      { key: SEARCH_TARGET_CONTACT, label: translate('contact') },
      { key: SEARCH_TARGET_ORDER, label: translate('order_fields') },
    ],
    defaultKey: SEARCH_TARGET_CONTACT,
    onApply: (targetKey, request) => {
      request.searchByContact = targetKey === SEARCH_TARGET_CONTACT
    },
  }

  return (
    <SearchModal<OrderFindRequest>
      title={translate('search', 'order')}
      open={open}
      closeMe={closeMe}
      onComplete={(request, page) => {
        fetch!(request, page)
        closeMe()
      }}
      idLabel={translate('order', 'number_abbr')}
      searchTarget={searchTarget}
      searchOptions={[
        {
          key: 'customerId',
          label: translate('contact_id'),
          render: ({ isActive }) => (
            <Select
              allowClear
              showSearch
              className='w-100'
              disabled={!isActive}
              loading={loadingCustomers}
              filterOption={false}
              options={customers?.contactsFind.data.map(c => ({
                label: [
                  c.contactId,
                  c.firstName,
                  c.lastName,
                ].filter(Boolean).join(' '),
                value: c.contactId,
              }))}
              onSearch={setSearchCustomer}
            />
          ),
          setValue: (item, request) => {
            request.customerId = item.value
            request.customerIdExactMatch = item.exactMatch
          },
        },
        {
          key: 'phone',
          label: translate('phone', 'number_abbr'),
          setValue: (item, request) => {
            request.phone = item.value
            request.phoneExactMatch = item.exactMatch
          },
        },
        {
          key: 'lastName',
          label: translate('last_name'),
          setValue: (item, request) => {
            request.lastName = item.value
            request.lastNameExactMatch = item.exactMatch
          },
        },
        {
          key: 'firstName',
          label: translate('first_name'),
          setValue: (item, request) => {
            request.firstName = item.value
            request.firstNameExactMatch = item.exactMatch
          },
        }
      ]}
      findPage={async id => {
        const { data, error } = await getPage({
          variables: {
            number: Number(id),
          },
        })
        return {
          page: data?.orderPage ?? 0,
          error: error?.graphQLErrors,
        }
      }}
      findPageLoading={loading}
      sort={{
        fields: [
          { value: 'date', display: translate('date') },
          { value: 'customerId', display: translate('customer', 'number_abbr') },
          { value: 'lastName', display: translate('last_name') },
          { value: 'firstName', display: translate('first_name') },
        ],
        defaults: [
          { field: 'date', direction: 'desc' }
        ]
      }}
    />
  )
}
